import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { AuthContextValue, AuthState } from '@/features/auth/types/auth.types';
import { authService } from '@/features/auth/services/authService';
import { firebaseService } from '@/features/auth/services/firebaseService';
import { mapFirebaseError } from '@/features/auth/utils/mapFirebaseError';
import { tokenStorage } from '@/features/auth/utils/tokenStorage';
import { FirebaseError } from 'firebase/app';
import { decodeJwt } from '@/features/auth/utils/jwtUtils';
import { clear as openPanelClear, identify as openPanelIdentify } from '@/shared/analytics/openpanel';
import { teamService } from '@/features/team/services/teamService/teamService';
import type { UserDetails } from '@/features/team/types/team.types';

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>(() => {
    // Synchronously check for existing token to set initial state
    const token = tokenStorage.getAccessTokenOptional();
    const hasToken = !!token;
    return {
      user: null,
      isAuthenticated: hasToken,
      isLoading: hasToken, // Set loading to true if we have a token that needs verification
    };
  });

  const [firebaseUser, setFirebaseUser] = useState<any>(null);
  const [dynamicPayload, setDynamicPayload] = useState<{ user_uuid?: string; customer_uuid?: string; customer_id?: string }>({});
  const [dynamicUser, setDynamicUser] = useState<{ email?: string; fullName?: string; role?: string; accessRole?: string }>({});
  const [member, setMember] = useState<UserDetails | null>(null);
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberError, setMemberError] = useState<string | null>(null);

  const setPayload = useCallback((user_uuid: string, customer_uuid: string, customer_id?: string) => {
    setDynamicPayload({ user_uuid, customer_uuid, customer_id });
  }, []);

  const setUserData = useCallback((email: string, fullName: string, role: string, accessRole?: string) => {
    setDynamicUser({ email, fullName, role, accessRole });
  }, []);

  useEffect(() => {
    const token = tokenStorage.getAccessTokenOptional();
    if (token) {
      checkAuth();
    }
  }, []); // Only run once on mount to restore session

  const checkAuth = useCallback(async () => {
    const token = tokenStorage.getAccessTokenOptional();
    if (token) {
      try {
        setMemberLoading(true);
        setMemberError(null);
        const decoded = decodeJwt<{ sub: string }>(token);
        const userUuid = decoded?.sub;
        if (!userUuid) {
          throw new Error('Missing user_uuid in access token');
        }

        const userData = await teamService.getMember(userUuid);
        setMember(userData);

        setPayload(userData.uuid, userData.customer_uuid);
        setUserData(
          userData.email ?? '',
          `${userData.name ?? ''} ${userData.lname ?? ''}`.trim(),
          userData.role,
          userData.origin?.access_role
        );

        const user = {
          id: userData.uuid,
          customer_uuid: userData.customer_uuid,
          email: userData.email,
          firstName: userData.name,
          lastName: userData.lname,
          phone: userData.phone,
        };

        const profileId = userUuid;
        if (profileId) {
          openPanelIdentify({
            profileId,
            email: userData.email ?? undefined,
          });
        }
        setState({
          user,
          isAuthenticated: true,
          isLoading: false
        });
        setMemberLoading(false);
      } catch {
        tokenStorage.clearAll();
        setMember(null);
        setMemberLoading(false);
        setMemberError('AUTH_CHECK_FAILED');
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false
        });
      }
    } else {
      setState(prev => ({
        ...prev,
        user: null,
        isAuthenticated: false,
        isLoading: false
      }));
      setMember(null);
      setMemberLoading(false);
      setMemberError(null);
    }
  }, [setPayload, setUserData]);

  const loginWithEmail = useCallback(async (email: string, password: string) => {
    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const { user, token } = await firebaseService.signIn(email, password);
      let dataUser =  {
        id: user.uid,
        email: user.email
      }
      
      setFirebaseUser(dataUser);

      tokenStorage.setFirebaseToken(token);
      const result = await preLogin();
      await checkAuth();
      return result;

    } catch (error) {
      if (error instanceof FirebaseError) {
        const apiError = mapFirebaseError(error);

        window.dispatchEvent(
          new CustomEvent('api:error', { detail: apiError })
        );

        throw apiError;
      }

      throw error;
    }
  }, []);

  const preLogin = useCallback(async () => {
    try {
      const { data } = await authService.preLogin();
      tokenStorage.setTempToken(data.temp_token);
      tokenStorage.setExpiresAt(data.expires_at);

      return data;
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, []);

  const login = useCallback(async () => {
    try {
      const { data } = await authService.login();
        console.log('Login Success');
        tokenStorage.setAccessToken(data.access_token);
        tokenStorage.setRefreshToken(data.refresh_token);
        tokenStorage.setAccessExpiresAt(data.expires_in);
        
        // Extract user_uuid from JWT subject (sub)
        const decoded = decodeJwt<{ sub: string }>(data.access_token);
        if (decoded?.sub) {
          setPayload(decoded.sub, "");
        } else if (data.user_uuid) {
          // Fallback if it's already in the body for some reason
          setPayload(data.user_uuid, "");
        }

        const profileId = decoded?.sub ?? data.user_uuid ?? firebaseUser?.id;
        if (profileId) {
          openPanelIdentify({
            profileId,
            email: firebaseUser?.email ?? undefined,
          });
        }

        setState(prev => ({ ...prev, isLoading: false, isAuthenticated: true, user: firebaseUser }));

        return true
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('BFF logout failed:', error);
    }

    tokenStorage.clearAll();
    setMember(null);
    setMemberLoading(false);
    setMemberError(null);

    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });

    openPanelClear();

    try {
      await firebaseService.logout();
    } catch (error) {
      console.error('Firebase logout failed:', error);
    }

    await checkAuth();
  }, [checkAuth]);

  const refreshToken = useCallback(async () => {
    try {
      await authService.refresh();

      setState((prev) => ({
        ...prev,
        isAuthenticated: true,
      }));
    } catch {
      await logout();
    }
  }, [logout]);

  const value: AuthContextValue = {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    loginWithEmail,
    login,
    logout,
    refreshToken,
    member,
    memberLoading,
    memberError,
    payload: {
      user_uuid: dynamicPayload.user_uuid || state.user?.id,
      customer_uuid: dynamicPayload.customer_uuid || state.user?.customer_uuid,
      customer_id: dynamicPayload.customer_id,
    },
    userData: {
      email: dynamicUser.email,
      fullName: dynamicUser.fullName,
      role: dynamicUser.role,
      accessRole: dynamicUser.accessRole,
    },
    setPayload,
    setUserData,
    setMember
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
